/**
 * Create/Edit Competition Page
 * Form for creating new competitions or challenges
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Trophy, ArrowLeft, Users, Target, Calendar, Swords,
  Plus, X, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { value: 'SALES', label: 'Sales' },
  { value: 'OPS', label: 'Operations' },
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'CREWS', label: 'Crews' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'LEADERSHIP', label: 'Leadership' },
  { value: 'GENERAL', label: 'General' }
];

const METRIC_TYPES = [
  { value: 'COUNT', label: 'Count (Integer)', example: '5 sales' },
  { value: 'CURRENCY', label: 'Currency ($)', example: '$10,000' },
  { value: 'PERCENT', label: 'Percentage (%)', example: '85%' },
  { value: 'HOURS', label: 'Hours', example: '40h' },
  { value: 'POINTS', label: 'Points', example: '100 pts' }
];

const SCORING_RULES = [
  { value: 'HIGHEST_WINS', label: 'Highest Wins', description: 'Participant with highest score wins' },
  { value: 'FIRST_TO_TARGET', label: 'First to Target', description: 'First to reach the target wins' },
  { value: 'CLOSEST_TO_TARGET', label: 'Closest to Target', description: 'Closest to target value wins' },
  { value: 'TEAM_SUM', label: 'Team Sum', description: 'Team with highest combined score wins' },
  { value: 'TEAM_AVERAGE', label: 'Team Average', description: 'Team with highest average score wins' }
];

const VISIBILITY_OPTIONS = [
  { value: 'COMPANY', label: 'Company-wide', description: 'Visible to all employees' },
  { value: 'DEPARTMENT', label: 'Department Only', description: 'Visible to department members' },
  { value: 'INVITE_ONLY', label: 'Invite Only', description: 'Only invited participants can see' }
];

export const CreateCompetition = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const isChallenge = searchParams.get('type') === 'challenge';
  
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'GENERAL',
    competition_type: isChallenge ? 'INDIVIDUAL' : 'INDIVIDUAL',
    metric_type: 'COUNT',
    target_value: '',
    scoring_rule: 'HIGHEST_WINS',
    visibility: isChallenge ? 'INVITE_ONLY' : 'COMPANY',
    allow_open_join: false,
    start_at: '',
    end_at: '',
    reward_description: '',
    participant_ids: [],
    team_ids: [],
    // Auto-tracking fields
    auto_track: false,
    auto_track_metric: '',
    points_per_unit: 1
  });
  
  // Available auto-track metrics
  const [availableMetrics, setAvailableMetrics] = useState([]);

  useEffect(() => {
    fetchUsersAndTeams();
    fetchAvailableMetrics();
  }, []);

  const fetchUsersAndTeams = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`)
      ]);
      setUsers(usersRes.data?.filter(u => u.id !== user?.id) || []);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error('Error fetching users/teams:', error);
    }
  };
  
  const fetchAvailableMetrics = async () => {
    try {
      const response = await axios.get(`${API}/competitions/metrics/available`);
      setAvailableMetrics(response.data || []);
    } catch (error) {
      console.error('Error fetching available metrics:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleParticipant = (userId) => {
    setFormData(prev => ({
      ...prev,
      participant_ids: prev.participant_ids.includes(userId)
        ? prev.participant_ids.filter(id => id !== userId)
        : [...prev.participant_ids, userId]
    }));
  };

  const toggleTeam = (teamId) => {
    setFormData(prev => ({
      ...prev,
      team_ids: prev.team_ids.includes(teamId)
        ? prev.team_ids.filter(id => id !== teamId)
        : [...prev.team_ids, teamId]
    }));
  };

  const handleSubmit = async (asDraft = false) => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.target_value || parseFloat(formData.target_value) <= 0) {
      toast.error('Please enter a valid target value');
      return;
    }
    if (isChallenge && formData.participant_ids.length === 0 && formData.team_ids.length === 0) {
      toast.error('Please select at least one opponent for the challenge');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        ...formData,
        target_value: parseFloat(formData.target_value),
        start_at: formData.start_at ? new Date(formData.start_at).toISOString() : (asDraft ? null : new Date().toISOString()),
        end_at: formData.end_at ? new Date(formData.end_at).toISOString() : null,
        is_challenge: isChallenge,
        challenger_id: isChallenge ? user?.id : null,
        // Auto-tracking fields
        auto_track: formData.auto_track,
        auto_track_metric: formData.auto_track ? formData.auto_track_metric : null,
        points_per_unit: formData.auto_track ? formData.points_per_unit : 1
      };

      const endpoint = isChallenge 
        ? `${API}/competitions/challenge?user_id=${user?.id}`
        : `${API}/competitions?user_id=${user?.id}`;
      
      const response = await axios.post(endpoint, payload);
      
      toast.success(isChallenge ? 'Challenge created!' : 'Competition created!');
      navigate(`/competition/competitions/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create competition');
    } finally {
      setLoading(false);
    }
  };

  // Filter users by search
  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTeams = teams.filter(t =>
    t.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="create-competition">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/competition')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {isChallenge ? (
                <>
                  <Swords className="h-6 w-6 text-purple-500" />
                  Challenge Someone
                </>
              ) : (
                <>
                  <Trophy className="h-6 w-6 text-amber-500" />
                  Create Competition
                </>
              )}
            </h1>
            <p className="text-muted-foreground">
              {isChallenge 
                ? 'Create a friendly challenge against colleagues'
                : 'Set up a new competition for your team'}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="grid gap-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder={isChallenge ? "e.g., Sales Showdown" : "e.g., Q1 Sales Competition"}
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what this competition is about..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={formData.competition_type} onValueChange={(v) => handleChange('competition_type', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                      <SelectItem value="TEAM">Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tracking & Goals */}
          <Card>
            <CardHeader>
              <CardTitle>Tracking & Goals</CardTitle>
              <CardDescription>Define how progress is measured</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auto-Track Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50 dark:bg-slate-900">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">Auto-Track from Platform Data</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically track scores from live platform metrics (no manual entry needed)
                  </p>
                </div>
                <Switch
                  checked={formData.auto_track}
                  onCheckedChange={(checked) => handleChange('auto_track', checked)}
                  data-testid="auto-track-toggle"
                />
              </div>
              
              {/* Auto-Track Metric Selection */}
              {formData.auto_track && (
                <div className="p-4 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 space-y-4">
                  <div>
                    <Label>Select Metric to Track *</Label>
                    <Select 
                      value={formData.auto_track_metric} 
                      onValueChange={(v) => {
                        const metric = availableMetrics.find(m => m.key === v);
                        handleChange('auto_track_metric', v);
                        if (metric) {
                          handleChange('metric_type', metric.metric_type);
                          handleChange('category', metric.category);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="auto-track-metric-select">
                        <SelectValue placeholder="Select a metric..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMetrics.map(m => (
                          <SelectItem key={m.key} value={m.key}>
                            <div>
                              <div className="font-medium">{m.label}</div>
                              <div className="text-xs text-muted-foreground">{m.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Points Per Unit</Label>
                    <Input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={formData.points_per_unit}
                      onChange={(e) => handleChange('points_per_unit', parseFloat(e.target.value) || 1)}
                      data-testid="points-per-unit-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Points awarded for each unit of the metric (e.g., 10 points per sale)
                    </p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Metric Type</Label>
                  <Select 
                    value={formData.metric_type} 
                    onValueChange={(v) => handleChange('metric_type', v)}
                    disabled={formData.auto_track}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METRIC_TYPES.map(m => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Example: {METRIC_TYPES.find(m => m.value === formData.metric_type)?.example}
                  </p>
                </div>
                <div>
                  <Label>Target Goal *</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 100"
                    value={formData.target_value}
                    onChange={(e) => handleChange('target_value', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Scoring Rule</Label>
                <Select value={formData.scoring_rule} onValueChange={(v) => handleChange('scoring_rule', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCORING_RULES.map(rule => (
                      <SelectItem key={rule.value} value={rule.value}>
                        <div>
                          <div>{rule.label}</div>
                          <div className="text-xs text-muted-foreground">{rule.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_at}
                    onChange={(e) => handleChange('start_at', e.target.value)}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_at}
                    onChange={(e) => handleChange('end_at', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visibility & Rewards (not for challenges) */}
          {!isChallenge && (
            <Card>
              <CardHeader>
                <CardTitle>Visibility & Rewards</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Visibility</Label>
                  <Select value={formData.visibility} onValueChange={(v) => handleChange('visibility', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VISIBILITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div>
                            <div>{opt.label}</div>
                            <div className="text-xs text-muted-foreground">{opt.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Allow Open Join</Label>
                    <p className="text-xs text-muted-foreground">Let anyone join without invitation</p>
                  </div>
                  <Switch
                    checked={formData.allow_open_join}
                    onCheckedChange={(v) => handleChange('allow_open_join', v)}
                  />
                </div>
                <div>
                  <Label>Reward / Props</Label>
                  <Input
                    placeholder="e.g., Bragging rights, Gift card, Shoutout..."
                    value={formData.reward_description}
                    onChange={(e) => handleChange('reward_description', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Participants */}
          <Card>
            <CardHeader>
              <CardTitle>
                {isChallenge ? 'Choose Your Opponent(s)' : 'Invite Participants'}
              </CardTitle>
              <CardDescription>
                {isChallenge 
                  ? 'Select who you want to challenge (max 3 active challenges)'
                  : 'Select users or teams to invite'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users or teams..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Selected */}
              {(formData.participant_ids.length > 0 || formData.team_ids.length > 0) && (
                <div className="flex flex-wrap gap-2">
                  {formData.participant_ids.map(id => {
                    const u = users.find(u => u.id === id);
                    return (
                      <Badge key={id} variant="secondary" className="pl-2 pr-1 py-1">
                        {u?.name || 'Unknown'}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 ml-1"
                          onClick={() => toggleParticipant(id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    );
                  })}
                  {formData.team_ids.map(id => {
                    const t = teams.find(t => t.id === id);
                    return (
                      <Badge key={id} variant="secondary" className="pl-2 pr-1 py-1">
                        <Users className="h-3 w-3 mr-1" />
                        {t?.name || 'Unknown Team'}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 ml-1"
                          onClick={() => toggleTeam(id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* User List */}
              {formData.competition_type === 'INDIVIDUAL' && (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">No users found</p>
                  ) : (
                    filteredUsers.map(u => (
                      <div
                        key={u.id}
                        className={`flex items-center gap-3 p-3 border-b last:border-0 cursor-pointer hover:bg-accent/50 ${
                          formData.participant_ids.includes(u.id) ? 'bg-accent' : ''
                        }`}
                        onClick={() => toggleParticipant(u.id)}
                      >
                        <Checkbox checked={formData.participant_ids.includes(u.id)} />
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>{u.name?.charAt(0) || '?'}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.role}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Team List */}
              {formData.competition_type === 'TEAM' && (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {filteredTeams.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">No teams found</p>
                  ) : (
                    filteredTeams.map(t => (
                      <div
                        key={t.id}
                        className={`flex items-center gap-3 p-3 border-b last:border-0 cursor-pointer hover:bg-accent/50 ${
                          formData.team_ids.includes(t.id) ? 'bg-accent' : ''
                        }`}
                        onClick={() => toggleTeam(t.id)}
                      >
                        <Checkbox checked={formData.team_ids.includes(t.id)} />
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{t.member_count || 0} members</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate('/competition')}>
              Cancel
            </Button>
            {!isChallenge && (
              <Button variant="outline" onClick={() => handleSubmit(true)} disabled={loading}>
                Save as Draft
              </Button>
            )}
            <Button onClick={() => handleSubmit(false)} disabled={loading}>
              {loading ? 'Creating...' : isChallenge ? 'Send Challenge' : 'Create & Activate'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateCompetition;
